import React, { useEffect, useState } from "react";
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TablePagination,
  TableRow,
  Tooltip,
  Typography,
  makeStyles,
} from "@material-ui/core";
import { AsyncSection, Badge, SegmentedControl } from "../AgentAnalytics/ds";
import KpiTile, { KpiStrip } from "../AgentAnalytics/KpiTile";
import { useTableStyles } from "../AgentAnalytics/tableStyles";
import { fetchAnalytics } from "./adTemplateApi";
import { formatLabel, int, MODE_LABEL, money, relativeDay } from "./formatters";
import AdsView from "./AdsView";
import AdCardDialog from "./AdCardDialog";
import FormatBadge from "./FormatBadge";

export const VIDEO_RUN_COLUMNS = [
  { key: "assistantId", label: "Project" },
  { key: "prompt", label: "Prompt" },
  { key: "length", label: "Length" },
  { key: "statusLabel", label: "Status" },
  { key: "videos", label: "Videos" },
  { key: "amount", label: "Billed", numeric: true },
  { key: "liveAds", label: "Live", numeric: true },
  { key: "createdAt", label: "Created" },
];

export const VIDEO_RUN_EXPORT_COLUMNS = [
  { key: "projectName", label: "Project name" },
  ...VIDEO_RUN_COLUMNS.map((c) =>
    c.key === "assistantId" ? { ...c, label: "Project ID" } : c
  ),
  { key: "error", label: "Failure reason" },
];

const MODES = [
  { value: "generated", label: "Generated" },
  { value: "launched", label: "Launched on Meta" },
];

const STATUSES = [
  { value: "all", label: "All" },
  { value: "completed", label: "Done" },
  { value: "partial", label: "Partial" },
  { value: "failed", label: "Failed" },
  { value: "inProgress", label: "In progress" },
];

const STATUS_LABEL = {
  completed: "Done",
  partial: "Partial",
  failed: "Failed",
  inProgress: "In progress",
};

const STATUS_TONE = {
  completed: "success",
  partial: "warning",
  failed: "error",
  inProgress: "neutral",
};

const DURATIONS = ["30", "45", "60"];

const useLocalStyles = makeStyles({
  panel: { padding: 16, display: "flex", flexDirection: "column", gap: 14 },
  controls: { display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap" },
  centeredTable: {
    "& td, & th": { textAlign: "center" },
  },
  num: {
    textAlign: "center",
    whiteSpace: "nowrap",
    fontVariantNumeric: "tabular-nums",
  },
  prompt: {
    maxWidth: 280,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
    margin: "0 auto",
  },
});

const toRow = (source) => {
  const run = { ...source, creativeFormat: "video" };
  return {
    ...run,
    length: formatLabel(run),
  statusLabel: STATUS_LABEL[run.status] || run.status,
    videos: `${run.delivered || 0} / ${run.requested || 0}`,
  };
};

const durationSplit = (byDuration = {}) =>
  DURATIONS.filter((seconds) => byDuration[seconds])
    .map((seconds) => `${seconds}s ${int(byDuration[seconds].delivered)}`)
    .join(" · ") || null;

export default function VideoAdsView({ range, search, refreshKey, onRows }) {
  const classes = { ...useTableStyles(), ...useLocalStyles() };
  const [mode, setMode] = useState("generated");
  const [summary, setSummary] = useState({ loading: true, data: null });
  const [status, setStatus] = useState("all");
  const [page, setPage] = useState(0);
  const [state, setState] = useState({
    loading: true,
    notFound: false,
    items: [],
    total: 0,
    error: null,
  });
  const [openAd, setOpenAd] = useState(null);
  const [opening, setOpening] = useState(null);

  useEffect(() => {
    setPage(0);
  }, [range.from, range.to, search, status]);

  useEffect(() => {
    let alive = true;
    setSummary((s) => ({ ...s, loading: true }));
    fetchAnalytics("videos/summary", { from: range.from, to: range.to }).then(
      (r) => alive && setSummary({ loading: false, data: r.data })
    );
    return () => {
      alive = false;
    };
  }, [range.from, range.to, refreshKey]);

  useEffect(() => {
    if (mode !== "generated") return undefined;
    let alive = true;
    setState((s) => ({ ...s, loading: true }));
    fetchAnalytics("videos", {
      from: range.from,
      to: range.to,
      page: page + 1,
      search,
      status: status === "all" ? "" : status,
    }).then((r) => {
      if (!alive) return;
      const items = ((r.data && r.data.items) || []).map(toRow);
      setState({
        loading: false,
        notFound: r.notFound,
        items,
        total: (r.data && r.data.total) || 0,
        error: r.error,
      });
      if (onRows) onRows(items, VIDEO_RUN_EXPORT_COLUMNS);
    });
    return () => {
      alive = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode, range.from, range.to, page, search, status, refreshKey]);

  const openVideo = (run) => {
    if (!run.assistantId || !run.delivered) return;
    setOpening(run.templateId);
    fetchAnalytics(`project/${run.assistantId}/variants`, {
      templateIds: run.templateId,
    }).then((r) => {
      setOpening(null);
      const variant = r.data && r.data.items && r.data.items[0];
      if (variant) setOpenAd(variant);
    });
  };

  const d = summary.data || {};
  const byStatus = d.byStatus || {};
  const inr = (d.money && d.money.INR) || null;
  const usd = (d.money && d.money.USD) || null;
  const launched = d.launched || {};

  return (
    <>
      <Box className={classes.panel}>
        <KpiStrip testid="video-kpis">
          <KpiTile
            icon="video"
            label="Video prompts"
            value={int(d.runs)}
            sub={d.projects != null ? `from ${int(d.projects)} projects` : null}
            loading={summary.loading}
            testid="video-kpi-runs"
          />
          <KpiTile
            icon="list"
            label="Videos made"
            value={int(d.delivered)}
            sub={durationSplit(d.byDuration)}
            loading={summary.loading}
            testid="video-kpi-made"
          />
          <KpiTile
            icon="triangle-alert"
            label="Failed"
            tone="error"
            value={int(byStatus.failed)}
            sub={
              summary.data
                ? `${int(byStatus.partial)} partial · ${int(byStatus.inProgress)} in progress`
                : null
            }
            loading={summary.loading}
            testid="video-kpi-failed"
          />
          <KpiTile
            icon="dollar-sign"
            label="Billed"
            tone="green"
            value={money(inr && inr.billed, "INR")}
            valueColor={inr && inr.billed > 0 ? "#0B8A4B" : undefined}
            sub={usd && usd.billed > 0 ? `and ${money(usd.billed, "USD")}` : null}
            loading={summary.loading}
            testid="video-kpi-billed"
          />
          <KpiTile
            icon="trending-up"
            label="Launched on Meta"
            value={int(launched.ads)}
            sub={summary.data ? `${int(launched.spend)} spend` : null}
            loading={summary.loading}
            testid="video-kpi-launched"
          />
          <KpiTile
            icon="message-circle"
            label="Meta leads"
            value={int(launched.leads)}
            sub={summary.data ? `${int(launched.messages)} messages` : null}
            loading={summary.loading}
            testid="video-kpi-leads"
          />
        </KpiStrip>

        <Box className={classes.controls}>
          <SegmentedControl
            value={mode}
            onChange={setMode}
            options={MODES}
            data-testid="video-mode"
          />
          {mode === "generated" && (
            <SegmentedControl
              value={status}
              onChange={setStatus}
              options={STATUSES}
              data-testid="video-status"
            />
          )}
        </Box>
      </Box>

      {mode === "launched" && (
        <AdsView
          range={range}
          search={search}
          refreshKey={refreshKey}
          onRows={onRows}
          format="video"
        />
      )}

      {mode === "generated" && (
        <AsyncSection
          loading={state.loading}
          notFound={state.notFound}
          error={state.error}
          empty={!state.items.length}
          emptyTitle="No video ads in this range"
          emptyDescription="Video generations show up here. Try a wider date range or a different status."
        >
          <Box data-testid="video-runs-table">
            <div className={`${classes.tableWrap} ${classes.centeredTable}`}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    {VIDEO_RUN_COLUMNS.map((c) => (
                      <TableCell key={c.key} align="center" className={classes.headCell}>
                        {c.label}
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {state.items.map((run) => (
                    <TableRow
                      key={run.templateId}
                      hover
                      className={classes.clickRow}
                      data-testid={`video-row-${run.templateId}`}
                      onClick={() => openVideo(run)}
                    >
                      <TableCell className={classes.cell}>
                        <Typography component="div" className={classes.nameText}>
                          {run.projectName || run.assistantId || "—"}
                        </Typography>
                        {run.projectName ? (
                          <Typography component="div" className={classes.monoSub}>
                            {run.assistantId}
                          </Typography>
                        ) : null}
                      </TableCell>
                      <TableCell className={classes.cell}>
                        <Typography
                          component="div"
                          className={classes.prompt}
                          title={run.prompt || ""}
                        >
                          {run.prompt || "—"}
                        </Typography>
                      </TableCell>
                      <TableCell className={classes.cell}>
                        <FormatBadge item={run} />
                      </TableCell>
                      <TableCell className={classes.cell}>
                        <Tooltip title={run.error || ""}>
                          <span>
                            <Badge tone={STATUS_TONE[run.status] || "neutral"} subtle>
                              {opening === run.templateId ? "Opening…" : run.statusLabel}
                            </Badge>
                          </span>
                        </Tooltip>
                      </TableCell>
                      <TableCell className={`${classes.cell} ${classes.num}`}>
                        {run.videos}
                      </TableCell>
                      <TableCell className={`${classes.cell} ${classes.num}`}>
                        {run.mode === "charged"
                          ? money(run.amount, run.currency)
                          : MODE_LABEL[run.mode] || run.mode}
                      </TableCell>
                      <TableCell className={`${classes.cell} ${classes.num}`}>
                        {int(run.liveAds)}
                      </TableCell>
                      <TableCell className={classes.cell}>{relativeDay(run.createdAt)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>

            <TablePagination
              component="div"
              count={state.total}
              page={page}
              onChangePage={(event, next) => setPage(next)}
              rowsPerPage={25}
              rowsPerPageOptions={[25]}
            />
          </Box>
        </AsyncSection>
      )}

      <AdCardDialog ad={openAd} onClose={() => setOpenAd(null)} />
    </>
  );
}
